import { useEffect, useLayoutEffect, lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ThemeProvider, PaletteRouteScope } from "@/hooks/useTheme";

const RoutePaletteScope = () => <PaletteRouteScope pathname={useLocation().pathname} />;
import { InstanceProvider } from "@/contexts/InstanceContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { SandboxProvider } from "@/contexts/SandboxContext";
import { DemoProvider } from "@/contexts/DemoContext";
import { DemoToolbar } from "@/components/DemoToolbar";
import { DemoOverlay } from "@/components/DemoOverlay";
import { DemoIntroSlide } from "@/components/DemoIntroSlide";
import { AuthGuard } from "./components/AuthGuard";
import { BetaAnnouncementPopup } from "./components/BetaAnnouncementPopup";
import { PwaInstallBanner } from "./components/PwaInstallBanner";
import { GanttSkeleton } from "@/components/loading";

// Lazy-loaded routes — code-split to reduce initial bundle size
const LandingB = lazy(() => import("./pages/LandingB"));
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SharedReport = lazy(() => import("./pages/SharedReport"));
const BugReportsAdmin = lazy(() => import("./pages/BugReportsAdmin"));
const ChatbotAnalytics = lazy(() => import("./pages/ChatbotAnalytics"));
const IntakeFormPage = lazy(() => import("./pages/IntakeFormPage"));
const PublicFormsPage = lazy(() => import("./pages/PublicFormsPage"));
const ApprovalReviewPage = lazy(() => import("./pages/ApprovalReviewPage"));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AutomationsPage = lazy(() => import("./pages/AutomationsPage"));
const BuilderPage = lazy(() => import("./pages/BuilderPage"));
const CourseLabPage = lazy(() => import("./pages/CourseLabPage"));
const CourseLabM2Page = lazy(() => import("./pages/CourseLabM2Page"));
const CourseLabM3Page = lazy(() => import("./pages/CourseLabM3Page"));
const CourseLabM4Page = lazy(() => import("./pages/CourseLabM4Page"));
const CourseLabSpacePage = lazy(() => import("./pages/CourseLabSpacePage"));
const CourseLabRecordPage = lazy(() => import("./pages/CourseLabRecordPage"));
const CourseLabJoinPage = lazy(() => import("./pages/CourseLabJoinPage"));
const OneSheet = lazy(() => import("./pages/OneSheet"));
const GoogleDriveCallbackPage = lazy(() => import("./pages/GoogleDriveCallbackPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ArchitecturePage = lazy(() => import("./pages/ArchitecturePage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const VpatPage = lazy(() => import("./pages/VpatPage"));
const MigrationPage = lazy(() => import("./pages/MigrationPage"));
const MigrationCaptureStudio = lazy(() => import("./pages/MigrationCaptureStudio"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPassword"));
const ActivateAccountPage = lazy(() => import("./pages/ActivateAccount"));
const ArchitectHealthPage = lazy(() => import("./pages/ArchitectHealthPage"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const GranolaCallbackPage = lazy(() => import("./pages/GranolaCallback"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const McpDocsPage = lazy(() => import("./pages/McpDocsPage"));
import { IntegrationChatAgentProvider } from "@/hooks/useIntegrationChatAgent";
import { NativeBootstrap } from "@/native/NativeBootstrap";

const withAgent = (node: import("react").ReactNode) => (
  <IntegrationChatAgentProvider>{node}</IntegrationChatAgentProvider>
);

const queryClient = new QueryClient();

const App = () => {
  // Hide the splash as soon as React mounts. Route/data loading is handled by
  // in-app skeletons so refresh never sits on the pulsing logo or a blank page.
  useEffect(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('workitect-app-ready'));
    });
  }, []);

  // GLOBAL PROMISE CRASH HANDLER: Catches "Uncaught (in promise)" errors
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      // Ignore benign fetch abort errors from Supabase realtime
      const reason = event.reason;
      if (reason?.name === 'AbortError' || (typeof reason?.message === 'string' && reason.message.includes('signal is aborted'))) {
        event.preventDefault();
        return;
      }
      console.error("[App] Unhandled rejection:", reason);
      toast.error("An unexpected error occurred. Please try again.");
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    let rafOne: number | null = null;
    let rafTwo: number | null = null;
    const timeoutIds: number[] = [];

    const syncStandaloneClass = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || Boolean((window.navigator as any).standalone);
      const safeTop = isStandalone ? 'env(safe-area-inset-top, 0px)' : '0px';
      const safeBottom = isStandalone ? 'env(safe-area-inset-bottom, 0px)' : '0px';

      root.classList.toggle('pwa-standalone', isStandalone);
      body.classList.toggle('pwa-standalone', isStandalone);
      root.style.setProperty('--pwa-safe-active-top', safeTop);
      root.style.setProperty('--pwa-safe-active-bottom', safeBottom);
    };

    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        syncStandaloneClass();
      }
    };

    const scheduleStartupSync = () => {
      syncStandaloneClass();
      rafOne = window.requestAnimationFrame(() => {
        syncStandaloneClass();
        rafTwo = window.requestAnimationFrame(syncStandaloneClass);
      });
      timeoutIds.push(window.setTimeout(syncStandaloneClass, 120));
      timeoutIds.push(window.setTimeout(syncStandaloneClass, 400));
      timeoutIds.push(window.setTimeout(syncStandaloneClass, 900));
    };

    scheduleStartupSync();

    const media = window.matchMedia('(display-mode: standalone)');
    const visualViewport = window.visualViewport;

    media.addEventListener?.('change', syncStandaloneClass);
    window.addEventListener('load', scheduleStartupSync);
    window.addEventListener('pageshow', syncStandaloneClass);
    window.addEventListener('resize', syncStandaloneClass);
    window.addEventListener('orientationchange', syncStandaloneClass);
    window.addEventListener('focus', syncStandaloneClass);
    document.addEventListener('visibilitychange', syncWhenVisible);
    visualViewport?.addEventListener('resize', syncStandaloneClass);
    visualViewport?.addEventListener('scroll', syncStandaloneClass);

    return () => {
      media.removeEventListener?.('change', syncStandaloneClass);
      window.removeEventListener('load', scheduleStartupSync);
      window.removeEventListener('pageshow', syncStandaloneClass);
      window.removeEventListener('resize', syncStandaloneClass);
      window.removeEventListener('orientationchange', syncStandaloneClass);
      window.removeEventListener('focus', syncStandaloneClass);
      document.removeEventListener('visibilitychange', syncWhenVisible);
      visualViewport?.removeEventListener('resize', syncStandaloneClass);
      visualViewport?.removeEventListener('scroll', syncStandaloneClass);
      if (rafOne !== null) window.cancelAnimationFrame(rafOne);
      if (rafTwo !== null) window.cancelAnimationFrame(rafTwo);
      timeoutIds.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <InstanceProvider>
          <SandboxProvider>
          <DemoProvider>
          <SubscriptionProvider>
          <TooltipProvider>
            <Sonner />
            <BetaAnnouncementPopup />
            <PwaInstallBanner />
            <DemoToolbar />
            <DemoOverlay />
            <DemoIntroSlide />
            <BrowserRouter>
              <NativeBootstrap />
              <RoutePaletteScope />
              <Suspense fallback={<GanttSkeleton className="h-screen" />}>
              <Routes>
                <Route path="/" element={<LandingB />} />
                <Route path="/welcome" element={<WelcomePage />} />
                <Route path="/app" element={<AuthGuard>{withAgent(<Index />)}</AuthGuard>} />
                <Route path="/app/task/:taskId" element={<AuthGuard>{withAgent(<Index />)}</AuthGuard>} />
                <Route path="/app/build" element={<AuthGuard><BuilderPage /></AuthGuard>} />
                <Route path="/app/lab/ai-in-project-management" element={<AuthGuard courseContext><CourseLabPage /></AuthGuard>} />
                <Route path="/app/lab/ai-in-project-management/m2" element={<AuthGuard courseContext><CourseLabM2Page /></AuthGuard>} />
                <Route path="/app/lab/ai-in-project-management/m3" element={<AuthGuard courseContext><CourseLabM3Page /></AuthGuard>} />
                <Route path="/app/lab/ai-in-project-management/m4" element={<AuthGuard courseContext><CourseLabM4Page /></AuthGuard>} />
                <Route path="/app/course-lab/:cohortId" element={<AuthGuard courseContext><CourseLabSpacePage /></AuthGuard>} />
                <Route path="/app/course-lab/:cohortId/records/:recordId" element={<AuthGuard courseContext><CourseLabRecordPage /></AuthGuard>} />
                <Route path="/course-lab/join" element={<CourseLabJoinPage />} />
                <Route path="/app/automations" element={<AuthGuard><AutomationsPage /></AuthGuard>} />
                <Route path="/app/migrate" element={<AuthGuard><MigrationPage /></AuthGuard>} />
                <Route path="/app/migrate/capture" element={<AuthGuard><MigrationCaptureStudio /></AuthGuard>} />
                <Route path="/app/integrations" element={<IntegrationsPage />} />
                <Route path="/integrations/granola/callback" element={<AuthGuard><GranolaCallbackPage /></AuthGuard>} />
                <Route path="/integrations/google/callback" element={<GoogleDriveCallbackPage />} />
                <Route path="/report/:id" element={<SharedReport />} />
                <Route path="/intake/:token" element={<IntakeFormPage />} />
                <Route path="/forms" element={<PublicFormsPage />} />
                <Route path="/approve/:token" element={<ApprovalReviewPage />} />
                <Route path="/invite" element={<AcceptInvitePage />} />
                <Route path="/invite/:token" element={<AcceptInvitePage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/activate" element={<ActivateAccountPage />} />
                <Route path="/admin/bug-reports" element={<AuthGuard><BugReportsAdmin /></AuthGuard>} />
                <Route path="/admin/chatbot-analytics" element={<AuthGuard><ChatbotAnalytics /></AuthGuard>} />
                <Route path="/architect/health" element={<AuthGuard><ArchitectHealthPage /></AuthGuard>} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/one-sheet" element={<OneSheet />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogArticlePage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/architecture" element={<ArchitecturePage />} />
                <Route path="/accessibility" element={<AccessibilityPage />} />
                <Route path="/vpat" element={<VpatPage />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/mcp-docs" element={<McpDocsPage />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
          </SubscriptionProvider>
          </DemoProvider>
          </SandboxProvider>
        </InstanceProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
